import React from "react";
import ReactDOM from "react-dom/client";
import { AuthProvider } from "@/components/providers/AuthProvider";
import { ThemeProvider } from "@/components/providers/ThemeProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SmoothScrollProvider } from "@/components/providers/SmoothScrollProvider";
import App from "./App";
import "./index.css";
/*
  These two live here, not only in the pages that use them, so they land in the entry
  stylesheet instead of a lazily-imported CSS chunk.

  `portfolio-management-services-two.css` backs 28 routes and carries `.pms2-root` — the
  hero's font-family, its --background/--foreground tokens and the desktop hero sizing.
  Imported only from the page components, Vite emitted it as a chunk that the browser did
  not fetch until the route's JS chunk did, which is well after first paint. Every one of
  those prerendered pages therefore painted its hero in the wrong font and colours and
  restyled once the chunk landed. `choice-group-page.css` (600 bytes) has the same problem
  across 7 more routes.

  Together that is 35 of the 37 live routes, so there is nothing meaningful left to defer:
  folding them in costs a few KB gzipped on the entry sheet and buys a correct first paint.
  `board-members-page-two.css` stays lazy — it is 20 KB for the single /group route.
*/
import "./pages/portfolio-management-services-two.css";
import "./pages/choice-group-page.css";

/**
 * Uncovers the page in the same commit that mounts it.
 *
 * On a fallback response the document arrives wearing data-cw-boot (see routeGuard in
 * scripts/prerender.mjs): the loader navy covers the viewport so another route’s markup
 * cannot show. A layout effect at the top of the tree runs after React has put the app
 * in the DOM but before the browser paints, and every child’s layout effect runs before
 * it — so the cover comes off with the first frame of real content, never leaving a
 * white gap and never letting a page measure itself through it. On a matching
 * prerendered page the attribute is absent and this does nothing.
 */
function BootReveal({ children }: { children: React.ReactNode }) {
  React.useLayoutEffect(() => {
    document.documentElement.removeAttribute("data-cw-boot");
  }, []);
  return <>{children}</>;
}

const tree = (
  <React.StrictMode>
    <BootReveal>
      <ThemeProvider>
        <AuthProvider>
          <TooltipProvider>
            <SmoothScrollProvider>
              <App />
            </SmoothScrollProvider>
          </TooltipProvider>
        </AuthProvider>
      </ThemeProvider>
    </BootReveal>
  </React.StrictMode>
);

const container = document.getElementById('root')!;

// Prerendered routes (scripts/prerender.mjs) already contain the rendered page, so we
// HYDRATE — React adopts the existing DOM instead of replacing it. That distinction is
// the whole point: createRoot would tear out the server markup and repaint, which resets
// Largest Contentful Paint to whenever React finished booting. Hydration keeps the LCP
// at the moment the HTML painted. Everything else (dev server, any route the prerender
// pass skipped) still mounts normally.
if (window.__CW_PRERENDERED__ && container.hasChildNodes()) {
  ReactDOM.hydrateRoot(container, tree);
} else {
  /*
    This document is not this route’s HTML: either the plain (unprerendered) build, or
    — far more often — the host’s SPA fallback answering a path it has no file for with
    dist/index.html, which is the prerendered HOME page. routeGuard has already stopped
    that markup from painting; drop the nodes so React never adopts them, then step the
    attribute down to "pending": #root is shown again — empty, so nothing can flash and
    nothing mounts or measures inside a display:none subtree — while the navy keeps
    covering the viewport until BootReveal fires.
  */
  if (document.documentElement.getAttribute("data-cw-boot") === "stale") {
    container.replaceChildren();
    document.documentElement.setAttribute("data-cw-boot", "pending");
  }
  ReactDOM.createRoot(container).render(tree);
}
